import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { updateProjectColumns } from "store/features/groupBy/groupBySlice";

import { deleteUserDefinedColumn } from "api/data";

import { EntityKind } from "models/entityKind";

import { useFieldGroupDataQueryKeys } from "components/user-defined-columns/queries";
import { usePurgeDeletedFieldsFromDefaults } from "components/user-settings/hooks";

import { useGroupByStore } from "../GroupByStoreProvider";
import { selectCheckedFields } from "../groupBySelectors";
import { useGetCategoryList } from "./useGetCategoryList";

interface UseFieldDeleteMutationParams {
  ids: string[];
  isOrg: boolean;
  entityKind: EntityKind;
  isProjectColumn?: boolean;
  fieldGroups?: string[];
  // Property keys of the fields being deleted (IGroupByListColumn.property,
  // distinct from `ids` which are the DB ids). Used to purge stale entries
  // from the user's saved default lists - see usePurgeDeletedFieldsFromDefaults.
  properties?: string[];
}

const useFieldDeleteMutation = ({
  ids,
  isOrg,
  entityKind = EntityKind.Well,
  isProjectColumn,
  fieldGroups,
  properties
}: UseFieldDeleteMutationParams) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { getCategoryListWithUnit } = useGetCategoryList();
  const purgeDeletedFieldsFromDefaults = usePurgeDeletedFieldsFromDefaults(entityKind);
  const checkedFields = useGroupByStore(selectCheckedFields);
  const setCheckedFields = useGroupByStore((s) => s.setCheckedFields);
  const { isLoading, mutate } = useMutation(
    () => {
      return deleteUserDefinedColumn(ids, isOrg, entityKind, isProjectColumn);
    },
    {
      onSuccess: () => {
        setCheckedFields(
          checkedFields.filter((checkedItem) => !ids.includes(checkedItem))
        );

        // Best-effort background cleanup - must never block the rest of this
        // success handler (category list refresh, project column update,
        // query invalidation) if it fails for any reason.
        try {
          purgeDeletedFieldsFromDefaults(properties ?? []);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to purge deleted fields from user defaults", error);
        }

        // Refresh the category list
        getCategoryListWithUnit(entityKind);

        if (isProjectColumn) {
          dispatch(updateProjectColumns(entityKind));
        }

        // Invalidate field group data queries for the affected field groups
        if (fieldGroups && fieldGroups.length > 0) {
          fieldGroups.forEach((fieldGroup) => {
            queryClient.invalidateQueries([
              useFieldGroupDataQueryKeys.fieldGroupData,
              fieldGroup,
              entityKind,
              isOrg
            ]);
          });
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (err: any) => {
        if (err.response?.data) {
          toast.error(`${err?.response.data}`);
        } else {
          toast.error(
            "We're sorry a network error occurred. The field could not be deleted."
          );
        }
      }
    }
  );

  return { isLoading, mutate };
};

export default useFieldDeleteMutation;
